import {
  black,
  green100,
  green120,
  green100Alpha50,
  grey10,
  grey100,
  grey100Alpha50,
  grey120,
  grey140,
  grey160,
  grey160Alpha90,
  grey180,
  grey20,
  grey40,
  grey60,
  grey80,
  indigo10,
  indigo100,
  indigo120,
  indigo140,
  indigo160,
  white,
  whiteAlpha25,
} from "../COREDesignSystem/Content/COREColour";

export type ThemeName = "light" | "dark";
export const themeVars: Record<ThemeName, Record<string, string>> = {
  light: {
    // Main menu
    "--main-menu-background": grey10,
    "--main-menu-default-text": grey140,
    "--main-menu-divider": grey80,
    "--main-menu-divider-opacity": "0.5",
    "--main-menu-active": grey160,
    "--main-menu-disabled": grey100,
    "--main-menu-active-background": white,
    "--main-menu-stroke": grey60,
    "--main-menu-bar-icon": grey160,
    "--logo-primary-icon": indigo100,

    "--top-header-background": grey20,
    "--top-header-background-dev": grey20,

    "--background": grey10,
    "--layout-background": grey10,
    "--content-background": grey10,

    // CORESwitch
    "--switch-border-off-color": grey80,
    "--switch-border-on-color": grey80,
    "--market-status-text-color": indigo100,
    "--market-status-close-text-color": grey100,
    "--market-status-hover-color": green100,
    "--primary-button-label": indigo100,
    "--market-status-dropdown-background-color": white,
    "--active-text": grey140,
    "--user-panel-hover-background-color": green100,
    "---market-status-divider-color": grey80,
    "--top-header-hamburger-icon-color": indigo100,
  },

  dark: {
    // Main menu
    "--main-menu-background": indigo160,
    "--main-menu-default-text": white,
    "--main-menu-divider": indigo100,
    "--main-menu-active": green100,
    "--main-menu-disabled": white,
    "--main-menu-active-background": indigo140,
    "--main-menu-stroke": indigo160,
    "--main-menu-bar-icon": indigo10,
    "--logo-primary-icon": white,

    "--core-menu-box-shadow-color": whiteAlpha25,
    "--core-sider-box-shadow-color": whiteAlpha25,

    // Main Display text
    "--h1-text": grey40,
    "--h2-text": grey120,
    "--h3-text": grey60,
    "--h4-text": grey100,
    "--h5-text": grey100,
    "--body-text": grey60,
    "--typography-text": grey60,
    // Inherited colour for the themed surfaces that previously set a background
    // without a matching foreground: `body` and .core-card-container. Icons and
    // bare text inside them fell back to antd's near-black @text-color and
    // vanished in dark mode (#2293). Matches --body-text so they read as the same
    // colour as COREBody text. Dark only: light and default leave it unset so
    // both fall back to their original values.
    "--base-text": grey60,
    "--button-label": grey60,
    "--text-label": grey60,
    "--placeholder-text": grey140,
    "--active-text": grey60,
    // The MFA setup modal is a plain antd Modal, so its surface stays antd's
    // hardcoded white in every theme while its text follows --active-text and
    // turns near-white here, leaving it illegible (#2293). Pin it back to the
    // value the other themes resolve --active-text to, so the modal reads the
    // same everywhere rather than gaining a dark-only appearance. Dark only:
    // light and default leave it unset and keep inheriting --active-text.
    "--mfa-modal-text": grey140,
    "--disabled-text": grey100Alpha50,
    "--card-header-text": grey60,
    "--modal-icon-color": grey140,

    // COREStats
    "--stats-form-color": grey100,
    "--stats-arrow": grey100,
    "--stats-disabled-text": grey120,
    "--stats-active-text": grey180,
    "--stats-disabled-background": grey20,
    "--stats-arrow-stroke": grey100,

    // CORESlect family
    "--select-disabled-text": grey100,
    "--cascader-background": green100,
    "--cascader-disabled-background": grey40,

    // COREInput family
    "--input-placeholder-text": grey140,
    "--input-disabled-text": grey100,

    // Date picker display
    "--date-picker-text": grey100,
    "--date-picker-range-text": grey100,

    // CORETable
    "--table-text": grey180,
    "--table-header-text": white,
    "--table-cell-text": grey60,
    "--table-header-background": indigo100,
    "--table-headless-section-background": grey160,
    "--table-row-banded-opacity": "0.08",
    "--table-row-hover-opacity": "0.25",
    "--table-row-active-color": grey40,
    "--table-row-active-opacity": "0.3",
    "--table-loading-background": grey60,
    "--table-loading-background-opacity": "0.1",

    // Background
    "--background": indigo160,
    "--layout-background": indigo160,
    "--content-background": indigo160,
    "--loading-background": grey100Alpha50,
    "--disabled-background": grey160Alpha90,
    "--core-detail-disabled-background": indigo160,
    "--core-container-background": indigo160,
    "--tags-background": indigo120,

    // Stroke
    "--disabled-stroke": grey140,

    // MRTCheckbox
    "--mrt-checkbox": grey80,
    "--mrt-checkbox-checked": green100,

    // Comment list
    "--comment-list-background-own": grey160,
    "--comment-list-background-own-opacity": "0.8",
    "--comment-list-background-other": grey160,
    "--comment-list-background-other-opacity": "0.4",

    // Top header
    "--top-header-background": indigo160,
    "--top-header-background-dev": indigo160,

    "--input-border-color": grey160,
    "--background-color": indigo160,
    "--core-chart-bgcolor": indigo160,
    "--border-color": grey160,
    "--primary-button-label": indigo100,
    "--core-chart-border-color": grey160,

    // List
    "--list-hover-background": "rgba(255,255,255,0.06)",

    // Asset change log sider
    "--asset-change-log-item-hover-background": indigo120,
    "--asset-change-log-item-pressed-background": indigo100,

    // CORESwitch
    "--switch-border-on-color": green100Alpha50,
    "--jd-box-shadow-1": whiteAlpha25,
    "--jd-color-background-default": indigo160,
    "--jd-color-background-light-gray": grey160,
    "--jd-color-panel": indigo160,
    "--jd-color-border": grey160,
    "--jd-color-icon": grey100,
    "--jd-color-button-background-hover": indigo120,
    "--jd-color-text-icons": grey100,
    "--jd-toolbar-button-trigger-icon": grey100,
    "--jd-color-background-button-hover": indigo120,
    "--modal-bg-opacity": "0.75",
    "--accordion-hover-background": indigo120,
    "--core-tree-selected-color": green100,
    "--core-menu-item-selected-background-color": indigo160,
    "--menu-item-selected-background": green120,
    "--core-table-editable-selected-bg": indigo120,
    "--core-upload-drag-bg": indigo160,
    "--scrollbar-color": grey160,
    "--scrollbar-bg-color": black,
    "--core-progress-bg-color": grey100Alpha50,
    "--core-tooltip-shadow":
      "0 3px 6px -4px rgba(255, 255, 255, 0.12), 0 6px 16px 0 rgba(255, 255, 255, 0.08), 0 9px 28px 8px rgba(255, 255, 255, 0.05)",
    "--core-tag-default-hover-bg": indigo160,
    "--market-status-text-color": green100,
    "--market-status-close-text-color": white,
    "--market-status-hover-color": green100,
    "--market-status-dropdown-background-color": indigo160,
    "--user-panel-hover-background-color": green100,
    "---market-status-divider-color": green100,
    "--core-text-editor-pre": indigo120,
    "--empty-img-simple-path-1": grey180,
    "--empty-img-simple-path-2": grey160,
    "--empty-img-simple-ellipse": "#F5F5F74D",
    "--core-message-box-shadow-color": whiteAlpha25,
    "--top-header-hamburger-icon-color": white,
    "--popover-shadow": whiteAlpha25,

    // COREScrollForMore
    "--scroll-for-more-background-footer":
      "linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.90) 25%, rgba(0, 0, 0, 0.90))",
    "--scroll-for-more-background-topper":
      "linear-gradient(to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.90) 25%, rgba(0, 0, 0, 0.90))",
  },
};
